import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  Box,
  Slider,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isSameDay, startOfDay, endOfDay } from 'date-fns';
import type { MapView } from '../services/types';
import { fetchWeatherDates, type ChartPoint } from '../services/weatherHistory';
import { useAtom, useSetAtom } from 'jotai';
import { selectedDateAtom, daySummaryModeAtom, timeNavigatedAtom } from '../map/atoms';

// Utility to find the closest time in an array of Dates to a target Date
function findClosestTime(target: Date, times: Date[]): Date | null {
  if (times.length === 0) return null;
  let closest = times[0];
  let minDiff = Math.abs(target.getTime() - closest.getTime());
  for (const t of times) {
    const diff = Math.abs(target.getTime() - t.getTime());
    if (diff < minDiff) {
      closest = t;
      minDiff = diff;
    }
  }
  return closest;
}

interface TimeSliderProps {
  selectedMap: MapView;
  events: ChartPoint[] | null;
}

const getUniqueDays = (dates: Date[]): Date[] => {
  const seen = new Set<string>();
  return dates
    .map(date => startOfDay(date))
    .filter(date => {
      const key = date.toISOString();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.getTime() - b.getTime());
};

const TimeSlider: React.FC<TimeSliderProps> = ({ selectedMap, events }) => {
  const [selectedDate, setSelectedDate] = useAtom(selectedDateAtom);
  const [daySummaryMode, setDaySummaryMode] = useAtom(daySummaryModeAtom);
  const setTimeNavigated = useSetAtom(timeNavigatedAtom);
  const [availableTimes, setAvailableTimes] = useState<Date[]>([]);
  const [loading, setLoading] = useState(false);
  // Thumb position while a drag is in progress. The selection (and with it the
  // map fetch) only commits when the drag ends, so scrubbing the slider does
  // not fire a request per mark crossed.
  const [dragTime, setDragTime] = useState<number | null>(null);

  // Latest selectedDate, read inside the day-load effect without making it a
  // dependency (which would refetch the day's snapshots on every slider move).
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  // Event dates drive which days are selectable in the date picker.
  const allEventDates = useMemo(
    () => (events ?? []).map(point => new Date(point.timestamp)),
    [events]
  );

  // Extract just the day from selectedDate to avoid refetching on time changes
  const currentDayKey = useMemo(() => {
    const date = selectedDate || new Date();
    return startOfDay(date).getTime(); // Use timestamp for comparison
  }, [selectedDate]);

  useEffect(() => {
    // Abort the previous day's in-flight snapshot-times request (the server
    // cancels its query too) when the user changes day or map again.
    const controller = new AbortController();

    const loadDates = async () => {
      setLoading(true);
      try {
        const dates = await fetchWeatherDates(
          selectedMap,
          new Date(currentDayKey),
          controller.signal
        );
        // A superseded request can still resolve (abort is best-effort); don't
        // let its stale day overwrite the state of the request that replaced it.
        if (controller.signal.aborted) return;
        setAvailableTimes(dates);
        // When the day was just picked, selectedDate sits at local
        // start-of-day -- before the first snapshot -- so the slider would
        // start at the far left and the map would fetch an empty pre-dawn
        // window. Snap to the latest snapshot of the day instead. A specific
        // time (e.g. a chart-point click) is not at midnight, so it is left
        // untouched. Snapping to today's newest snapshot IS the live view, so
        // it clears the navigation flag the setSelectedDate write just raised.
        const current = selectedDateRef.current;
        if (dates.length > 0 && current && current.getTime() === currentDayKey) {
          setSelectedDate(dates[0]);
          if (isSameDay(new Date(currentDayKey), new Date())) {
            setTimeNavigated(false);
          }
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Error fetching available times:', error);
        setAvailableTimes([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadDates();

    return () => {
      controller.abort();
    };
  }, [selectedMap, currentDayKey, setSelectedDate, setTimeNavigated]);

  // Get unique days from all event data for date picker
  const uniqueDays = useMemo(() => getUniqueDays(allEventDates), [allEventDates]);

  // Derive selected day from selectedDate
  const selectedDay = useMemo(() => {
    if (!selectedDate) return uniqueDays[0] || null;
    return startOfDay(selectedDate);
  }, [selectedDate, uniqueDays]);

  // Available times are what we fetched (within the selected day range), sorted chronologically
  const dayTimes = useMemo(() => {
    return [...availableTimes].sort((a, b) => a.getTime() - b.getTime());
  }, [availableTimes]);

  // Create marks for the slider to show available times
  const sliderMarks = useMemo(() => {
    if (!selectedDay) return [];

    return dayTimes
      .filter(time => isSameDay(time, selectedDay))
      .map(time => ({
        value: time.getTime(),
        label: '', // No label to keep it clean
      }));
  }, [dayTimes, selectedDay]);

  // Derive current time from selectedDate, snapping to closest available time
  const currentTime = useMemo(() => {
    if (!selectedDate || !selectedDay) return dayTimes[0] || null;
    if (isSameDay(selectedDate, selectedDay)) {
      // If selectedDate is on the current day, snap to closest time
      return findClosestTime(selectedDate, dayTimes) || dayTimes[0] || null;
    }
    // If selectedDate is not on current day, use first available time
    return dayTimes[0] || null;
  }, [selectedDate, selectedDay, dayTimes]);

  // Handlers
  const handleDayChange = useCallback(
    (newDate: Date | null) => {
      if (!newDate) return;
      const day = startOfDay(newDate);
      // When day changes, select the start of that day - available times will be fetched
      setSelectedDate(startOfDay(day));
    },
    [setSelectedDate]
  );

  // Track the thumb during the drag without committing -- only the release
  // commits a selection and triggers a fetch.
  const handleSliderChange = useCallback((_event: Event, newValue: number | number[]) => {
    setDragTime(newValue as number);
  }, []);

  const handleSliderChangeCommitted = useCallback(
    (_event: React.SyntheticEvent | Event, newValue: number | number[]) => {
      setDragTime(null);
      const targetDate = new Date(newValue as number);
      // Snap to closest available time
      const committed = findClosestTime(targetDate, dayTimes) ?? targetDate;
      setSelectedDate(committed);
      // Committing today's newest snapshot means "back to live": the current
      // tables hold exactly that snapshot, so clear the navigation flag the
      // setSelectedDate write just raised.
      const newest = dayTimes[dayTimes.length - 1];
      if (newest && committed.getTime() === newest.getTime() && isSameDay(committed, new Date())) {
        setTimeNavigated(false);
      }
    },
    [setSelectedDate, setTimeNavigated, dayTimes]
  );

  // The MUI date field can momentarily hold an invalid date while the user is
  // typing segments; date-fns `format` throws on that, which would crash the
  // whole panel. Render nothing for an invalid date instead.
  const formatDate = (date: Date) =>
    Number.isNaN(date.getTime()) ? '' : format(date, 'MMM d, yyyy HH:mm');

  // Function to determine if a date should be disabled
  const shouldDisableDate = useCallback(
    (date: Date) => {
      return !uniqueDays.some(availableDay => isSameDay(date, availableDay));
    },
    [uniqueDays]
  );

  if (loading) {
    return (
      <Box
        sx={{
          ml: 13,
          mr: 0,
          width: 'calc(100% - 115px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
        }}
      >
        <CircularProgress size={40} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ml: 13,
        mr: 0,
        width: 'calc(100% - 115px)',
      }}
    >
      <Box sx={{ width: '100%', mt: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'white' }} gutterBottom>
              Select Date
            </Typography>
            <DatePicker
              value={selectedDay}
              onChange={handleDayChange}
              shouldDisableDate={shouldDisableDate}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  sx: {
                    '& .MuiPickersInputBase-root': {
                      color: 'white !important',
                      '& input': {
                        color: 'white !important',
                      },
                    },
                    '& .MuiPickersOutlinedInput-root': {
                      color: 'white !important',
                      '& input': {
                        color: 'white !important',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.8) !important',
                      '&.Mui-focused': {
                        color: 'white !important',
                      },
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white !important',
                    },
                  },
                },
                popper: {
                  sx: {
                    '& .MuiPaper-root': {
                      backgroundColor: 'rgba(0, 62, 103, 0.8) !important',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    },
                    '& .MuiPickersLayout-root': {
                      backgroundColor: 'transparent !important',
                      color: 'white !important',
                    },
                    '& .MuiTypography-root': {
                      color: 'white !important',
                    },
                    '& .MuiPickersCalendarHeader-root': {
                      color: 'white !important',
                      '& .MuiTypography-root': {
                        color: 'white !important',
                      },
                    },
                    '& .MuiPickersArrowSwitcher-root': {
                      '& .MuiIconButton-root': {
                        color: 'white !important',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      },
                    },
                    '& .MuiDayCalendar-header': {
                      '& .MuiTypography-root': {
                        color: 'rgba(255, 255, 255, 0.8) !important',
                        fontWeight: 600,
                      },
                    },
                    '& .MuiPickerDay-root': {
                      color: 'white !important',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.25) !important',
                        color: 'white !important',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.35) !important',
                        },
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.3) !important',
                      },
                      '&.MuiPickerDay-today:not(.Mui-selected)': {
                        border: '1px solid rgba(255, 255, 255, 0.6)',
                        color: 'white !important',
                      },
                    },
                    '& .MuiPickersYear-yearButton': {
                      color: 'white !important',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.25) !important',
                        color: 'white !important',
                      },
                    },
                    '& .MuiPickersMonth-monthButton': {
                      color: 'white !important',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.25) !important',
                        color: 'white !important',
                      },
                    },
                  },
                },
              }}
            />
          </Box>
        </LocalizationProvider>

        {/* Day Summary Mode Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={daySummaryMode}
              onChange={e => setDaySummaryMode(e.target.checked)}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-checked': {
                  color: 'white',
                },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'white' }}>
              Day Summary
            </Typography>
          }
          sx={{ mb: 1 }}
        />

        <Typography variant="body2" sx={{ color: 'white' }} gutterBottom>
          {dragTime != null
            ? formatDate(new Date(dragTime))
            : currentTime
              ? formatDate(currentTime)
              : 'No time available'}
        </Typography>
        <Slider
          value={dragTime ?? (currentTime ? currentTime.getTime() : 0)}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderChangeCommitted}
          min={selectedDay ? startOfDay(selectedDay).getTime() : 0}
          max={selectedDay ? endOfDay(selectedDay).getTime() : 0}
          step={null} // Allow free movement to available times
          marks={sliderMarks}
          valueLabelDisplay="auto"
          valueLabelFormat={value => formatDate(new Date(value))}
          disabled={!currentTime || daySummaryMode}
          sx={{
            '& .MuiSlider-mark': {
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              height: 8,
              width: 2,
              marginTop: -2,
            },
            '& .MuiSlider-markActive': {
              backgroundColor: 'white',
            },
            '& .MuiSlider-track': {
              backgroundColor: 'white',
            },
            '& .MuiSlider-thumb': {
              backgroundColor: 'white',
              '&:hover': {
                boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.16)',
              },
            },
            '& .MuiSlider-rail': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
            },
            // The white track/thumb/mark colors above also apply while
            // disabled, which made a disabled slider look highlighted instead
            // of inert -- dim everything when disabled (day summary mode).
            '&.Mui-disabled': {
              '& .MuiSlider-track': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
              },
              '& .MuiSlider-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
              },
              '& .MuiSlider-rail': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
              '& .MuiSlider-mark': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
              },
              '& .MuiSlider-markActive': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default TimeSlider;
